export type APIResponse<T = undefined> =
	| {
			success: true;
			message: string;
			data: T;
			error?: never;
	  }
	| {
			success: false;
			message: string;
			data: never;
			error?: string;
	  };

export type AuthResponse = {
	userId: string;
	token: string;
	firstName?: string;
	lastName?: string;
};

export type APIErrorResponse = {
	success: false;
	message: string;
	errors?: Record<string, string>;
};

export type User = {
	id: string;
	phoneNumber: string;
	countryCode: string;
	country: string;
	username: string;
	details?: {
		firstName: string;
		lastName: string;
		email?: string;
		profilePicture?: string;
	};
};

// Event types
export type Event = {
	id: string;
	slug: string;
	title: string;
	description: string | null;
	coverImage: string | null;
	startDate: string;
	locationAddress: string;
	locationCoordinates: string | null;
	locationUrl: string | null;
	guestLimit: number | null;
	allowPlusOne: boolean;
	visibility: string;
	guestListVisibility: boolean;
	status: string;
	hostId: string;
	hostName: string;
	locationLink: string | null;
	username: string;
	collectDietaryPrefs: boolean;
};

export type InviteResponse = {
	id: string;
	slug: string;
	title: string;
	description: string | null;
	coverImage: string | null;
	startDate: string;
	guestLimit: number | null;
	allowPlusOne: boolean;
	visibility: string;
	status: string;
	hostId: string;
	hostName: string;
	rsvpCount: number;
};

export type EventAttendee = {
	avatar: string;
};

export type HomePageEvent = Event & {
	attendees: EventAttendee[];
	status: "hosting" | "invited";
	isPast: boolean;
	isUpcoming: boolean;
	rsvpStatus?: "pending" | "accepted" | "declined" | "maybe";
};

export type CategorizedEvents = {
	upcoming: HomePageEvent[];
	hosting: HomePageEvent[];
	past: HomePageEvent[];
};

export type RsvpRequest = {
	status: "accepted" | "declined" | "maybe";
	bringingPlusOne?: boolean;
	plusOneName?: string;
	dietaryPreference?: "vegetarian" | "non_vegetarian";
	plusOneDietaryPreference?: "vegetarian" | "non_vegetarian";
	dietaryNotes?: string;
};

export type Rsvp = {
	id: number;
	userId: string;
	status: "accepted" | "declined" | "maybe" | "pending";
	bringingPlusOne: boolean;
	plusOneName: string | null;
	dietaryPreference: string | null;
	plusOneDietaryPreference: string | null;
	dietaryNotes: string | null;
	rsvpedAt: string;
	name: string;
	avatar: string | null;
	username: string;
};

export type EventDetailsResponse = {
	event: Event;
	rsvps: Rsvp[];
};

export type ResolveMapUrlResponse = {
	originalUrl: string;
	resolvedUrl: string;
	coordinates?: { lat: number; lng: number };
	formattedCoordinates?: string;
};

export type UserProfileResponse = {
	id: string;
	phoneNumber: string;
	countryCode: string;
	country: string;
	email: string | null;
	username: string;
	details: {
		firstName: string;
		lastName: string;
		dateOfBirth: string;
		profilePicture: string | null;
	};
	socials: {
		platform: "instagram" | "twitter" | "snapchat" | "spotify" | "apple_music";
		username: string;
		link: string;
	}[];
	stats: {
		eventsHosted: number;
		eventsAttended: number;
	};
};

export const DIETARY_OPTIONS = ["vegetarian", "non_vegetarian"] as const;
export type DietaryPreference = (typeof DIETARY_OPTIONS)[number];
